import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Select, MenuItem, FormControl,
  InputLabel, Switch, FormControlLabel, Button, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, Chip, IconButton, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions
} from '@mui/material';
import { AutoFixHigh, PersonAdd, Delete, Save } from '@mui/icons-material';
import { assignmentsAPI, reviewersAPI, applicationsAPI } from '../../services/api';
import { useSnackbar } from '../../utils/notify';

export default function AssignmentManager() {
  const { id: formId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [config, setConfig] = useState({ reviewers_per_application: 2, assignment_rule: 'round_robin', auto_assign_enabled: false });
  const [assignments, setAssignments] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [apps, setApps] = useState([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualApp, setManualApp] = useState('');
  const [manualReviewer, setManualReviewer] = useState('');

  const load = () => {
    assignmentsAPI.getConfig(formId).then((r) => setConfig(r.data.data));
    assignmentsAPI.list(formId).then((r) => setAssignments(r.data.data));
    reviewersAPI.list('can_review').then((r) => setReviewers(r.data.data.filter((rv) => rv.is_active && rv.invite_accepted)));
    applicationsAPI.list(formId, { per_page: 200 }).then((r) => setApps(r.data.data));
  };
  useEffect(load, [formId]);

  const saveConfig = async () => {
    try {
      await assignmentsAPI.saveConfig(formId, config);
      enqueueSnackbar('Config saved', { variant: 'success' });
    } catch(e) { enqueueSnackbar('Failed', { variant: 'error' }); }
  };

  const autoAssign = async () => {
    try {
      const res = await assignmentsAPI.autoAssign(formId);
      enqueueSnackbar(`${res.data.data.assignments_created} assignments created`, { variant: 'success' });
      load();
    } catch (err) {
      enqueueSnackbar((err.response && err.response.data && err.response.data.error) || 'Auto-assign failed', { variant: 'error' });
    }
  };

  const handleManualAssign = async () => {
    try {
      await assignmentsAPI.manualAssign(manualApp, manualReviewer);
      enqueueSnackbar('Assigned', { variant: 'success' });
      setManualOpen(false);
      load();
    } catch (err) {
      enqueueSnackbar((err.response && err.response.data && err.response.data.error) || 'Failed', { variant: 'error' });
    }
  };

  const removeAssignment = async (id) => {
    await assignmentsAPI.remove(id);
    enqueueSnackbar('Removed', { variant: 'info' });
    load();
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>Assignment Manager</Typography>

      <Grid container spacing={3}>
        {/* Config */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Configuration</Typography>
              <TextField fullWidth label="Reviewers per Application" type="number" value={config.reviewers_per_application}
                onChange={(e) => setConfig({ ...config, reviewers_per_application: parseInt(e.target.value) || 1 })} sx={{ mb: 2 }}
                inputProps={{ min: 1 }} />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Assignment Rule</InputLabel>
                <Select value={config.assignment_rule} label="Assignment Rule"
                  onChange={(e) => setConfig({ ...config, assignment_rule: e.target.value })}>
                  <MenuItem value="round_robin">Round Robin (balanced)</MenuItem>
                  <MenuItem value="random">Random</MenuItem>
                  <MenuItem value="manual">Manual Only</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel control={
                <Switch checked={config.auto_assign_enabled}
                  onChange={(e) => setConfig({ ...config, auto_assign_enabled: e.target.checked })} />
              } label="Auto-assign on new submissions" />
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button variant="contained" startIcon={<Save />} onClick={saveConfig}>Save</Button>
                <Button variant="outlined" startIcon={<AutoFixHigh />} onClick={autoAssign}
                  disabled={config.assignment_rule === 'manual'}>
                  Run Auto-Assign
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Manual Assignment</Typography>
              <Button variant="outlined" startIcon={<PersonAdd />} onClick={() => setManualOpen(true)}>
                Assign Manually
              </Button>
            </CardContent>
          </Card>

          <Alert severity="info" sx={{ mt: 2 }}>
            Conflict detection is automatic: a reviewer who is also an applicant will not be assigned their own application.
          </Alert>
        </Grid>

        {/* Assignments table */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Current Assignments ({assignments.length})
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Applicant</TableCell>
                      <TableCell>Reviewer</TableCell>
                      <TableCell>Assigned By</TableCell>
                      <TableCell>Review Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assignments.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>{a.applicant_name}</TableCell>
                        <TableCell>{a.reviewer_name}</TableCell>
                        <TableCell><Chip label={a.assigned_by} size="small" variant="outlined" /></TableCell>
                        <TableCell>
                          <Chip label={a.review_status} size="small"
                            color={a.review_status === 'completed' ? 'success' : a.review_status === 'in_progress' ? 'warning' : 'default'} />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => removeAssignment(a.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Manual assign dialog */}
      <Dialog open={manualOpen} onClose={() => setManualOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manual Assignment</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Application</InputLabel>
            <Select value={manualApp} label="Application" onChange={(e) => setManualApp(e.target.value)}>
              {apps.map((a) => <MenuItem key={a.id} value={a.id}>{a.applicant_name} ({a.applicant_email})</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Reviewer</InputLabel>
            <Select value={manualReviewer} label="Reviewer" onChange={(e) => setManualReviewer(e.target.value)}>
              {reviewers.map((r) => <MenuItem key={r.id} value={r.id}>{r.name} ({r.email})</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleManualAssign} disabled={!manualApp || !manualReviewer}>Assign</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
